import { Box, Typography } from "@mui/material";
import { checkIf, debounce, googleMapsApiKey } from "../../utils";
import { CrossIcon, LeftArrowIcon, LocationIcon, MarkerIcon, SatelliteIcon } from "../../assets/icons";
import { APIProvider, Map, MapCameraChangedEvent } from "@vis.gl/react-google-maps";
import { Fragment, useEffect, useRef, useState } from "react";
import MapsSearch from "./search";
import { LoadingButton } from "@mui/lab";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";

interface IProps {
  onClose: (place?: any) => void;
  selectedCoordinates?: { lat: number; lng: number; detail: string };
}

export const mapPositionDefault = { lat: 25.354826, lng: 51.183884 }; // Qatar

export const placeNameGenerator = (response: google.maps.GeocoderResponse) => {
  const result0 = response.results[0];
  const checkPluscode = result0.address_components[0].types.includes("plus_code");

  if (checkPluscode) {
    let routeComponent = result0.address_components.find((component: any) => component.types.includes("route"));

    if (!routeComponent) {
      const anyResultToIncludeRoute = response.results.find((result: any) =>
        result.address_components.find((component: any) => component.types.includes("route"))
      );
      routeComponent = anyResultToIncludeRoute?.address_components.find((component: any) =>
        component.types.includes("route")
      );
    }

    let subLocalityComponent = result0.address_components.find((component: any) =>
      component.types.includes("sublocality")
    );

    if (!subLocalityComponent) {
      const anyResultToIncludeSubLocality = response.results.find((result: any) =>
        result.address_components.find((component: any) => component.types.includes("sublocality"))
      );
      subLocalityComponent = anyResultToIncludeSubLocality?.address_components.find((component: any) =>
        component.types.includes("sublocality")
      );
    }

    let localityComponent = result0.address_components.find((component: any) => component.types.includes("locality"));

    if (!localityComponent) {
      const anyResultToIncludeLocality = response.results.find((result: any) =>
        result.address_components.find((component: any) => component.types.includes("locality"))
      );
      localityComponent = anyResultToIncludeLocality?.address_components.find((component: any) =>
        component.types.includes("locality")
      );
    }
    return `${routeComponent?.short_name ? routeComponent.short_name + ", " : ""}${
      subLocalityComponent?.short_name ? subLocalityComponent.short_name + ", " : ""
    }${localityComponent?.short_name ? localityComponent.short_name : ""}`;
  } else {
    return result0.formatted_address;
  }
};

export default function AddressMap(props: IProps) {
  const dispatch = useDispatch();
  const searchRef = useRef<HTMLInputElement>(null);
  const [loader, setLoader] = useState(false);
  const [locationInfo, setLocationInfo] = useState<any>({ place: undefined, location: undefined, loader: false });

  let map: google.maps.Map;
  let marker: google.maps.marker.AdvancedMarkerElement;
  let infoWindow: google.maps.InfoWindow;
  let geocoder: google.maps.Geocoder;

  const geocoderRef = useRef<google.maps.Geocoder>();
  const mapRef = useRef<google.maps.Map>();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement>();
  const satelliteRef = useRef<"roadmap" | "satellite" | "hybrid" | "terrain">("roadmap");

  async function initMap(): Promise<void> {
    let zoomLevel = 11;
    let mapPosition = mapPositionDefault;
    if (props.selectedCoordinates && props.selectedCoordinates.lat && props.selectedCoordinates.lng) {
      mapPosition = props.selectedCoordinates;
      zoomLevel = 15;
      setLocationInfo((state: any) => ({
        ...state,
        place: props.selectedCoordinates?.detail,
        location: new google.maps.LatLng(
          Number(props.selectedCoordinates?.lat),
          Number(props.selectedCoordinates?.lng)
        ),
        loader: false,
      }));
    }

    // Initialize the map.
    map = new google.maps.Map(document.getElementById("map") as HTMLElement, {
      center: mapPosition,
      zoom: zoomLevel,
      mapId: "4504f8b37365c3d0",
      mapTypeControl: false,
      streetViewControl: false,
      cameraControl: false,
      fullscreenControl: false,
    });

    map.setMapTypeId(satelliteRef.current);

    mapRef.current = map;

    //@ts-ignore
    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement();
    //@ts-ignore
    placeAutocomplete.id = "place-autocomplete-input";
    placeAutocomplete.locationBias = mapPosition;

    // const card = document.getElementById("place-autocomplete-card") as HTMLElement;
    // //@ts-ignore
    // card.appendChild(placeAutocomplete);
    // map.controls[google.maps.ControlPosition.TOP_LEFT].push(card);

    // Create the marker and infowindow

    const parser = new DOMParser();
    // A marker with a custom inline SVG.
    const pinSvgString =
      '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M7.0835 12.7499C7.08398 10.8492 7.63071 8.98867 8.65857 7.38983C9.68643 5.791 11.1521 4.52124 12.8811 3.73174C14.6101 2.94225 16.5296 2.66628 18.411 2.9367C20.2924 3.20711 22.0564 4.01252 23.4931 5.25703C24.9298 6.50153 25.9785 8.13269 26.5144 9.9563C27.0504 11.7799 27.051 13.7191 26.5161 15.543C25.9812 17.367 24.9335 18.9987 23.4976 20.2441C22.0616 21.4895 20.2981 22.2959 18.4168 22.5674V29.7499C18.4168 30.1257 18.2676 30.486 18.0019 30.7517C17.7362 31.0174 17.3759 31.1666 17.0002 31.1666C16.6244 31.1666 16.2641 31.0174 15.9984 30.7517C15.7328 30.486 15.5835 30.1257 15.5835 29.7499V22.5674C13.2229 22.2267 11.0642 21.0466 9.50303 19.2435C7.94188 17.4404 7.08288 15.135 7.0835 12.7499ZM15.4985 11.2483C15.8966 10.8496 16.4368 10.6254 17.0002 10.6249C17.3759 10.6249 17.7362 10.4757 18.0019 10.21C18.2676 9.94433 18.4168 9.584 18.4168 9.20828C18.4168 8.83255 18.2676 8.47222 18.0019 8.20654C17.7362 7.94087 17.3759 7.79161 17.0002 7.79161C15.6851 7.79161 14.424 8.314 13.4941 9.24387C12.5642 10.1737 12.0418 11.4349 12.0418 12.7499C12.0418 13.1257 12.1911 13.486 12.4568 13.7517C12.7224 14.0174 13.0828 14.1666 13.4585 14.1666C13.8342 14.1666 14.1946 14.0174 14.4602 13.7517C14.7259 13.486 14.8752 13.1257 14.8752 12.7499C14.8752 12.1861 15.099 11.6449 15.4985 11.2483Z" fill="transparent"/></svg>';
    const pinSvg = parser.parseFromString(pinSvgString, "image/svg+xml").documentElement;

    marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      gmpDraggable: false,
      position: mapPosition,
      content: pinSvg,
    });
    markerRef.current = marker;

    infoWindow = new google.maps.InfoWindow({});
    geocoder = new google.maps.Geocoder();
    geocoderRef.current = geocoder;

    marker.addListener("dragend", (event: any) => {
      //console.log("dragend event", event);
      //console.log("marker", marker.position);
      const position = marker.position as google.maps.LatLng;
      //console.log("dragend position", { lat: position.lat, lng: position.lng });
      geocodeLatLng(geocoder, map, infoWindow, position, marker, false);
    });

    //Add an event listener to the map to list for the center change event "center_changed"
    map.addListener("center_changed", () => {
      const center = map.getCenter();
      marker.position = center;
      setLocationInfo((state: any) => ({ ...state, place: undefined, location: undefined, loader: true }));
      db(geocoder, map, infoWindow, center as google.maps.LatLng, marker, false);
    });

    // Add the gmp-placeselect listener, and display the results on the map.
    //@ts-ignore
    // placeAutocomplete.addEventListener("gmp-select", async ({ placePrediction }) => {
    //   const place = placePrediction.toPlace();
    //   await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });

    //   // If the place has a geometry, then present it on a map.
    //   if (place.viewport) {
    //     map.fitBounds(place.viewport);
    //   } else {
    //     map.setCenter(place.location);
    //     map.setZoom(17);
    //   }

    //   console.log(place);

    //   let content =
    //     '<div id="infowindow-content">' +
    //     '<span id="place-displayname" class="title">' +
    //     place.displayName +
    //     "</span><br />" +
    //     '<span id="place-address">' +
    //     place.formattedAddress +
    //     "</span>" +
    //     "</div>";

    //   updateInfoWindow(content, place.location);
    //   marker.position = place.location;
    // });
  }

  function handlePlaceSelected(place: any) {
    // console.log("place", place);
    if (place.viewport) {
      mapRef.current?.fitBounds(place.viewport);
    } else {
      mapRef.current?.setCenter(place.location);
      mapRef.current?.setZoom(17);
    }
    // marker.position = place.location;
    setLocationInfo((state: any) => ({ place: place.formatted_address, location: place.location, loader: false }));
    //updateInfoWindow(content, place.location, marker);
  }

  function handleCurrentLocation() {
    if (navigator.geolocation) {
      setLoader(true);
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          geocodeLatLng(geocoderRef.current as google.maps.Geocoder, map, infoWindow, pos, marker);
        },
        () => {
          handleLocationError(true, infoWindow, map.getCenter()!);
        }
      );
    } else {
      // Browser doesn't support Geolocation
      handleLocationError(false, infoWindow, map.getCenter()!);
    }
  }

  function handleUseAddress(place: any, location: any) {
    const placeRef = {
      formattedAddress: place,
      location: location,
    };
    props.onClose(placeRef);
  }

  function createContent(place: any) {
    // let content =
    //   '<div id="infowindow-content">' +
    //   '<span id="place-displayname" class="title">' +
    //   place.displayName +
    //   "</span><br />" +
    //   '<span id="place-address">' +
    //   place.formattedAddress +
    //   "</span>" +
    //   "</div>";
    let div = document.createElement("div");
    let displayName = document.createElement("span");
    displayName.className = "title";
    displayName.textContent = place.displayName;
    div.appendChild(displayName);
    div.appendChild(document.createElement("br"));

    let formattedAddress = document.createElement("span");
    formattedAddress.className = "address";
    formattedAddress.textContent = place.formattedAddress;
    div.appendChild(formattedAddress);

    let p = document.createElement("p");
    p.style.margin = "0";
    p.style.padding = "0";
    p.style.marginBottom = "8px";
    div.appendChild(p);

    let button = document.createElement("button");
    button.className = "gmaps-label-tag";
    button.textContent = "Use this address";
    button.addEventListener("click", () => {
      handleUseAddress(place.formatted_address, place.location);
    });

    div.appendChild(button);
    return div;
  }

  // Helper function to create an info window.
  function updateInfoWindow(content: any, center: any, marker: google.maps.marker.AdvancedMarkerElement) {
    infoWindow.setContent(content);
    // infoWindow.setPosition(center);
    infoWindow.open({
      map,
      anchor: marker,
      // shouldFocus: false,
    });
  }

  function handleLocationError(
    browserHasGeolocation: boolean,
    infoWindow: google.maps.InfoWindow,
    pos: google.maps.LatLng
  ) {
    infoWindow.setPosition(pos);
    infoWindow.setContent(
      browserHasGeolocation
        ? "Error: The Geolocation service failed."
        : "Error: Your browser doesn't support geolocation."
    );
    infoWindow.open(map);
    setLoader(false);
  }

  function geocodeLatLng(
    geocoder: google.maps.Geocoder,
    map: google.maps.Map,
    infoWindow: google.maps.InfoWindow,
    location: google.maps.LatLng | { lat: number; lng: number },
    marker: google.maps.marker.AdvancedMarkerElement,
    reCenter: boolean = true
  ) {
    setLocationInfo({ place: undefined, location: undefined, loader: true });
    geocoder
      .geocode({ location })
      .then((response) => {
        // console.log("response", response);
        if (response.results[0]) {
          const result = response.results[0];
          if (reCenter) {
            handlePlaceSelected({
              displayName: "Current Location",
              formattedAddress: result.formatted_address,
              location: result.geometry.location,
              viewport: result.geometry.viewport,
            });
          } else {
            let content = createContent({
              formattedAddress: result.formatted_address,
              displayName: "",
              location: result.geometry.location,
            });
            // DON'T SET THE MARKER POSITION HERE
            //marker.position = result.geometry.location;

            setLocationInfo((state: any) => ({ place: placeNameGenerator(response), location, loader: false }));
            // console.log("position", { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() });
            // INFO WINDOW REMOVED
            //updateInfoWindow(content, result.geometry.location, marker);
          }
        } else {
          console.log("No results found");
        }
      })
      .catch((e) => {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: "We’re having trouble loading the map. Please check your connection or try again soon.",
          })
        );
      })
      .finally(() => {
        setLocationInfo((state: any) => ({ ...state, loader: false }));
        setLoader(false);
      });
  }

  const db = debounce(geocodeLatLng, 500);

  useEffect(() => {
    initMap();
  }, []);

  return (
    <Fragment>
      <Box sx={{ display: "flex", flexDirection: "column", position: "relative", alignItems: "center" }}>
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px",
            backgroundColor: "primary.main",
            position: ["fixed", "absolute"],
            width: "100%",
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              color: "primary.text",
              fontSize: "20px",
              fontWeight: 600,
              lineHeight: "150%",
            }}
          >
            Find your address
          </Typography>
          <Box sx={{ color: "primary.text", display: "flex", cursor: "pointer" }}>
            <CrossIcon onClick={() => props.onClose()} width={24} height={24} />
          </Box>
        </Box>
        <Box sx={{ width: "100%", marginTop: "62px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              position: "absolute",
              top: "72px",
              left: "8px",
              right: "60px",
              zIndex: 3,
              // boxShadow: "0px 6px 12px 0px rgba(0, 0, 0, 0.16)",
              borderRadius: "12px",
            }}
          >
            <MapsSearch
              onPlaceSelected={(place) => {
                handlePlaceSelected(place);
              }}
              floatingLayer={true}
            />
          </Box>
          <Box
            onClick={() => {
              satelliteRef.current = satelliteRef.current === "roadmap" ? "satellite" : "roadmap";
              mapRef.current?.setMapTypeId(satelliteRef.current);
            }}
            sx={{
              cursor: "pointer",
              display: "flex",
              position: "absolute",
              width: "40px",
              height: "40px",
              right: "10px",
              top: "79px",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 3,
              flexDirection: "column",
              borderRadius: "2px",
              boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
              backgroundColor: "text.white",
            }}
          >
            <Typography
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.dark",
              }}
            >
              <SatelliteIcon width={20} height={20} />
            </Typography>
          </Box>
          <Box sx={{ width: "100%", height: ["calc(100vh - 62px)", "567px"] }} ref={mapRef} id="map"></Box>
        </Box>
        <Box
          sx={{
            position: "fixed",
            display: "flex",
            flexDirection: "column",
            zIndex: 2,
            top: "50%",
          }}
        >
          <MarkerIcon />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          padding: "8px",
          flexDirection: "column",
          gap: "8px",
          position: "absolute",
          bottom: "0",
          left: "0",
          right: "0",
          zIndex: 2,
          backgroundColor: "text.white",
        }}
      >
        <Typography
          sx={{ fontSize: "12px", fontWeight: 600, lineHeight: "normal", color: "text.dark", textAlign: "center" }}
        >
          {locationInfo.place}
        </Typography>
        <LoadingButton
          fullWidth
          color="primary"
          disabled={locationInfo.loader}
          variant="contained"
          onClick={() => handleUseAddress(locationInfo.place, locationInfo.location)}
          sx={{ borderRadius: "90px" }}
        >
          {locationInfo.loader ? <span>Detecting location...</span> : <span>Use this address</span>}
        </LoadingButton>
      </Box>
      {/* Hide current location finding */}
      {/* <LoadingButton
        loading={loader}
        variant="contained"
        disableElevation
        onClick={handleCurrentLocation}
        sx={{
          padding: 0,
          minWidth: "42px",
          width: "42px",
          height: "42px",
          position: "absolute",
          bottom: "100px",
          right: "16px",
          borderRadius: "90px",
          backgroundColor: "#fff !important",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.25)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loader ? (
          <span>&nbsp;</span>
        ) : (
          <Typography
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.dark",
            }}
          >
            <LocationIcon width={26} height={26} />
          </Typography>
        )}
      </LoadingButton> */}
    </Fragment>
  );
}
